import React, { useContext, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../hooks/ThemeContext';
import { CartContext } from '../../hooks/CartContext';
import SectionTitle from '../../components/common/SectionTitle';
import ProductCard from '../../components/common/ProductCard';
import CTABox from '../../components/common/CTABox';
import LoadingSpinner from '../../components/globals/LoadingSpinner';
import { useProducts } from '../../hooks/useProducts';
import { ShoppingBag, ShieldCheck, Zap } from 'lucide-react';

const Shop = () => {
  const { theme } = useContext(ThemeContext);
  const { addToCart } = useContext(CartContext);
  const isDark = theme === 'dark';
  const navigate = useNavigate();

  const { products, loading, error } = useProducts();
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = useMemo(() => {
    const keys = Array.from(new Set(products.map((p) => (p.category || 'autre').toLowerCase())));
    const dynamic = keys.map((k) => ({ key: k, label: k.charAt(0).toUpperCase() + k.slice(1) }));
    return [
      { key: 'all', label: 'Tout' },
      ...dynamic,
    ];
  }, [products]);

  // Filtrage des produits
  const filteredProducts = useMemo(() => {
    if (activeCategory === 'all') return products;
    return products.filter(p => (p.category || '').toLowerCase() === activeCategory);
  }, [activeCategory, products]);

  const stats = useMemo(() => ([
    { label: 'Produits en ligne', value: products.length || 0, icon: <ShoppingBag className="w-5 h-5 text-rose-400" /> },
    { label: 'Catégories', value: Math.max(categories.length - 1, 0), icon: <ShieldCheck className="w-5 h-5 text-rose-400" /> },
    { label: 'Sélection active', value: filteredProducts.length || 0, icon: <Zap className="w-5 h-5 text-rose-400" /> },
  ]), [categories.length, filteredProducts.length, products.length]);

  const handleAddToCart = async (productId) => {
    const success = await addToCart(productId, 1);
    if (success) {
      // Afficher un message de succès
    }
  };

  const handleViewDetails = (productId) => {
    if (productId) {
      navigate(`/shop/${productId}`);
    }
  };

  const getMeta = (product) => {
    if (product.sale_type === 'unique') return 'Exemplaire unique';
    const stock = product.stock ?? product.available_stock ?? 0;
    return `Stock: ${stock}`;
  };

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className={`${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 border-b border-white/10' : 'bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50 border-b border-gray-200'}`}>
        <div className="container mx-auto px-4 py-16 lg:py-20 max-w-7xl">
          <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] items-start">
            <div className="space-y-6 lg:pr-8">
              <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm shadow-xl ${isDark ? 'border-white/10 bg-white/5 text-gray-200' : 'border-gray-200 bg-white text-gray-700'}`}>
                <ShoppingBag className="w-4 h-4 text-rose-400" />
                Boutique créative premium
              </div>

              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl xl:text-6xl font-bold leading-tight">
                  Boutique de <span className="text-rose-500">créations digitales</span>
                </h1>
                <p className={`max-w-2xl text-lg leading-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Trouver rapidement vos visuels, bannieres, logos et ressources prêtes a l'emploi.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                {stats.map((item) => (
                  <div key={item.label} className={`rounded-3xl border p-5 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
                    <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                      {item.icon}
                    </div>
                    <p className="text-2xl font-bold mb-1">{item.value}</p>
                    <p className={`${isDark ? 'text-sm text-gray-300' : 'text-sm text-gray-600'}`}>{item.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className={`rounded-3xl border p-6 md:p-8 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
              <SectionTitle 
                title="Achetez plus sereinement"
                subtitle="Navigation fluide, fiches produit claires et parcours d'achat harmonise avec le reste du site."
                align="left"
              />
              <div className="grid gap-4 sm:grid-cols-2 mt-6">
                {[
                  'Visuels protégés et lisibles',
                  'Détails accessibles en un clic',
                  'Ajout au panier immédiat',
                  'Parcours homogène avec le site'
                ].map((item) => (
                  <div key={item} className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20' : 'border-gray-200 bg-slate-50'}`}>
                    <div className="flex items-start gap-3">
                      <ShieldCheck className="w-5 h-5 text-rose-400 mt-0.5" />
                      <span className={`${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{item}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-7xl">

        {error && (
          <div className={`text-center mb-6 rounded-3xl border p-4 ${isDark ? 'text-red-300 bg-red-500/10 border-red-500/20' : 'text-red-600 bg-red-50 border-red-200'}`}>{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Filtres */}
            {categories.length > 1 && (
              <nav className={`flex flex-wrap justify-center gap-3 mb-10 rounded-3xl border p-4 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`} aria-label="Filtres produits">
                {categories.map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => setActiveCategory(cat.key)}
                    className={`px-6 py-3 rounded-2xl font-medium transition-all border shadow-xl ${
                      activeCategory === cat.key
                        ? isDark
                          ? 'bg-rose-600 text-white border-rose-600'
                          : 'bg-rose-600 text-white border-rose-600'
                        : isDark
                        ? 'bg-white/5 text-white border-white/10 hover:border-rose-500/40'
                        : 'bg-white text-gray-800 border-gray-200 hover:border-rose-300'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </nav>
            )}

            {/* Grille de produits */}
            {filteredProducts.length === 0 ? (
              <div className={`text-center py-12 text-sm rounded-3xl border shadow-xl ${isDark ? 'opacity-90 border-white/10 bg-white/5 text-gray-300' : 'border-gray-200 bg-white text-gray-600'}`}>
                Aucun produit disponible pour le moment.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {filteredProducts.map(product => (
                  <div key={product.id_product ?? product.id} className="relative">
                    <ProductCard
                      title={product.name_product || product.name}
                      description={product.description_product || product.description}
                      price={product.price_product || product.price}
                      promoPrice={product.price_promo ?? product.price_promo_ttc ?? null}
                      promoActive={Boolean(product.promo_active)}
                      promoDiscount={product.promo_discount}
                      image={product.image} // toujours formaté par useProducts
                      images={product.images && product.images.length > 0 ? product.images : [product.image].filter(Boolean)}
                      category={product.category}
                      meta={getMeta(product)}
                      onAddToCart={() => handleAddToCart(product.id_product ?? product.id)}
                      onViewDetails={() => handleViewDetails(product.id_product ?? product.id)}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* CTA Section */}
        <div className="mt-16">
          <div className={`rounded-3xl border p-8 md:p-10 shadow-xl ${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 border-white/10' : 'bg-gradient-to-r from-rose-50 to-purple-50 border-gray-200'}`}>
            <CTABox
              title="Besoin d'une création sur mesure ?"
              description="Notre équipe peut créer des designs personnalisés adaptés à vos besoins spécifiques"
              buttonText="Demander un devis"
              buttonLink="/contact"
              variant="primary"
            />
          </div>
        </div>

        {/* Informations supplémentaires */}
        <div className={`mt-12 grid grid-cols-1 md:grid-cols-3 gap-6`}>
          <div className={`p-6 rounded-3xl shadow-xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
            <div className="text-3xl mb-2">🎨</div>
            <h3 className="text-lg font-semibold mb-2">Design Unique</h3>
            <p className={`text-sm leading-7 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              Chaque création est réalisée sur mesure selon vos besoins
            </p>
          </div>
          <div className={`p-6 rounded-3xl shadow-xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
            <div className="text-3xl mb-2">⚡</div>
            <h3 className="text-lg font-semibold mb-2">Livraison Rapide</h3>
            <p className={`text-sm leading-7 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              Délai moyen de 3-5 jours ouvrés pour vos créations
            </p>
          </div>
          <div className={`p-6 rounded-3xl shadow-xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
            <div className="text-3xl mb-2">🔄</div>
            <h3 className="text-lg font-semibold mb-2">Révisions Incluses</h3>
            <p className={`text-sm leading-7 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              Jusqu'à 3 révisions pour garantir votre satisfaction
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Shop;
